import React, { useState, useEffect, useMemo } from 'react';
import { usersApi, teamApi } from '../../api/users.js';
import toast, { Toaster } from 'react-hot-toast';

export default function SalesTeamManagementPage() {
  const [teamLeads, setTeamLeads] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination & Search for Team Leads table
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit, setLimit] = useState(20);

  // Assign Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTL, setSelectedTL] = useState(null);
  const [modalSearch, setModalSearch] = useState('');
  const [selectedRepIds, setSelectedRepIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [originalAssignedIds, setOriginalAssignedIds] = useState([]);
  const [debouncedModalSearch, setDebouncedModalSearch] = useState('');

  // View Team Modal State
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewSelectedTL, setViewSelectedTL] = useState(null);
  const [viewMembers, setViewMembers] = useState([]);
  const [viewSearch, setViewSearch] = useState('');
  const [debouncedViewSearch, setDebouncedViewSearch] = useState('');
  const [viewCurrentPage, setViewCurrentPage] = useState(1);
  const [viewTotalPages, setViewTotalPages] = useState(1);
  const [viewLoading, setViewLoading] = useState(false);

  // Debounce searches
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, limit]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedModalSearch(modalSearch), 400);
    return () => clearTimeout(timer);
  }, [modalSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedViewSearch(viewSearch), 400);
    return () => clearTimeout(timer);
  }, [viewSearch]);

  useEffect(() => {
    setViewCurrentPage(1);
  }, [debouncedViewSearch]);

  const fetchTeamLeads = async () => {
    setLoading(true);
    try {
      const res = await usersApi.getAll({
        limit: limit === 100000 ? 100000 : limit,
        page: currentPage,
        search: debouncedSearch,
        role: 'sales_tl',
        is_active: true
      });
      setTeamLeads(res.data ?? []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Failed to load team leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const res = await usersApi.getAll({
        limit: 20,
        role: 'sales',
        search: debouncedModalSearch,
        is_active: true
      });
      setSalesReps(res.data ?? []);
    } catch (err) {
      console.error('Failed to load sales reps:', err);
    }
  };

  const fetchViewMembers = async () => {
    if (!viewSelectedTL) return;
    setViewLoading(true);
    try {
      const res = await teamApi.getMembersByTlId(viewSelectedTL.id, {
        limit: 20,
        page: viewCurrentPage,
        search: debouncedViewSearch
      });
      setViewMembers(res.data ?? []);
      setViewTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Failed to load view members:', err);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchSalesReps();
    }
  }, [isModalOpen, debouncedModalSearch]);

  useEffect(() => {
    fetchTeamLeads();
  }, [currentPage, debouncedSearch, limit]);

  useEffect(() => {
    if (isViewModalOpen) {
      fetchViewMembers();
    }
  }, [isViewModalOpen, debouncedViewSearch, viewCurrentPage]);

  const handleOpenModal = async (tl) => {
    setSelectedTL(tl);
    setModalSearch('');
    setIsModalOpen(true);

    // Fetch currently assigned reps for this TL to know who is pre-selected
    try {
      const res = await usersApi.getAll({ limit: 500, tl_id: tl.id });
      const assignedIds = (res.data ?? []).map(r => r.id);
      setSelectedRepIds(assignedIds);
      setOriginalAssignedIds(assignedIds);
    } catch (err) {
      console.error('Failed to load assigned reps', err);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTL(null);
  };

  const handleOpenViewModal = (tl) => {
    setViewSelectedTL(tl);
    setViewSearch('');
    setViewCurrentPage(1);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewSelectedTL(null);
    setViewMembers([]);
  };

  const handleToggleRep = (repId) => {
    setSelectedRepIds(prev =>
      prev.includes(repId) ? prev.filter(id => id !== repId) : [...prev, repId]
    );
  };

  const handleSaveAssignments = async () => {
    setSaving(true);
    try {
      const toAssign = selectedRepIds.filter(id => !originalAssignedIds.includes(id));
      const toUnassign = originalAssignedIds.filter(id => !selectedRepIds.includes(id));

      const promises = [];
      for (const id of toAssign) {
        promises.push(usersApi.update(id, { tl_id: selectedTL.id }));
      }
      for (const id of toUnassign) {
        promises.push(usersApi.update(id, { tl_id: null }));
      }

      await Promise.all(promises);
      toast.success('Assignments updated successfully!');
      handleCloseModal();
      fetchTeamLeads();
    } catch (err) {
      toast.error('Failed to update assignments');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getTeamSize = (tlId) => {
    return '...';
  };

  return (
    <div className="page-body">
      <Toaster position="top-right" />
      <div className="card">
        <div className="card-header">
          <span className="card-title">Sales Team Management</span>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Manage Team Leads and assign Sales Reps to them</p>
        </div>
        <div className="card-body">
          <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: '300px' }}
              placeholder="Search Team Leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Team Lead Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Areas</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamLeads.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No team leads found</td>
                      </tr>
                    ) : teamLeads.map(tl => (
                      <tr key={tl.id}>
                        <td>#{tl.id}</td>
                        <td style={{ fontWeight: 600 }}>{tl.name}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tl.email}</td>
                        <td style={{ fontSize: 13 }}>{tl.phone || '-'}</td>
                        <td>
                          {tl.areas && tl.areas.length > 0 ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {tl.areas.map(a => (
                                <span key={a.id} className="badge badge-gray" style={{ fontSize: '11px' }}>{a.name}</span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>None</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => handleOpenViewModal(tl)}
                            >
                              See Team
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleOpenModal(tl)}
                            >
                              Assign Sales Reps
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
                    <select
                      className="form-control"
                      style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                      value={limit === 100000 ? 'all' : limit}
                      onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setCurrentPage(1); }}
                    >
                      <option value={20}>20</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={500}>500</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ border: '1px solid #e2e8f0', background: 'transparent', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="btn btn-sm"
                    style={{ border: '1px solid #e2e8f0', background: 'transparent', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '20px',
            width: '600px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Assign Reps to {selectedTL?.name}</h3>
              <button
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                &times;
              </button>
            </div>

            <input
              type="text"
              className="form-control"
              placeholder="Search sales reps..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ marginBottom: '1rem' }}
            />

            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '10px' }}>
              {salesReps.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No sales reps found.</div>
              ) : (
                salesReps.map(rep => (
                  <label key={rep.id} style={{
                    display: 'flex', alignItems: 'center', padding: '8px',
                    borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                    background: selectedRepIds.includes(rep.id) ? '#f8fafc' : 'transparent'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedRepIds.includes(rep.id)}
                      onChange={() => handleToggleRep(rep.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{rep.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{rep.email}</div>
                    </div>
                    {rep.TeamLead && rep.TeamLead.id !== selectedTL.id && (
                      <span style={{ fontSize: '11px', color: '#f59e0b', backgroundColor: '#fef3c7', padding: '2px 6px', borderRadius: '4px' }}>
                        Currently: {rep.TeamLead.name}
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
              <button className="btn btn-outline" onClick={handleCloseModal} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveAssignments} disabled={saving}>
                {saving ? 'Saving...' : `Save ${selectedRepIds.length} Assignments`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Team Modal */}
      {isViewModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '8px', padding: '20px',
            width: '600px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{viewSelectedTL?.name}'s Team</h3>
              <button
                onClick={handleCloseViewModal}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                &times;
              </button>
            </div>

            <input
              type="text"
              className="form-control"
              placeholder="Search team members..."
              value={viewSearch}
              onChange={(e) => setViewSearch(e.target.value)}
              style={{ marginBottom: '1rem' }}
            />

            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
              {viewLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading team members...</div>
              ) : viewMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No team members found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--border-color)' }}>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>NAME</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>EMAIL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewMembers.map(member => (
                      <tr key={member.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px', fontSize: '13px' }}>#{member.id}</td>
                        <td style={{ padding: '12px', fontWeight: 500 }}>{member.name}</td>
                        <td style={{ padding: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>{member.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {viewTotalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="btn btn-sm"
                  style={{ border: '1px solid #e2e8f0', background: 'transparent', cursor: viewCurrentPage === 1 ? 'not-allowed' : 'pointer' }}
                  disabled={viewCurrentPage === 1}
                  onClick={() => setViewCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                  Page {viewCurrentPage} of {viewTotalPages}
                </span>
                <button
                  className="btn btn-sm"
                  style={{ border: '1px solid #e2e8f0', background: 'transparent', cursor: viewCurrentPage === viewTotalPages ? 'not-allowed' : 'pointer' }}
                  disabled={viewCurrentPage === viewTotalPages}
                  onClick={() => setViewCurrentPage(prev => Math.min(viewTotalPages, prev + 1))}
                >
                  Next
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleCloseViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
